'use client';

import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import Image from 'next/image';
import { useEffect, useState } from 'react';
import '@splidejs/react-splide/css';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import UserDesignDetailModal from '@/features/gfUserDesign/components/UserDesignDetailModal/UserDesignDetailModal';
import { SIZE_SELECT_FROM } from '@/features/gfUserDesign/constants';
import { useUserDesignSlider } from '@/hooks/useUserDesignSlider';
import type { UserDesignItem } from '@/types';
import type { Route } from 'next';
import styles from './ProductUserDesignsSlider.module.scss';

// 1スライドあたりの表示数
const PER_PAGE_SP = 3;
const PER_PAGE_PC = 5;

function ProductUserDesignsSlider({
  userDesignList,
  openModalUserPrintId,
}: {
  userDesignList: UserDesignItem[];
  /** サイズセレクトから戻った時に開くモーダルの userPrintId（クエリ openModal から渡される） */
  openModalUserPrintId?: string;
}) {
  const { isSp, slideItems, splideRef, handleSlidePrev, handleSlideNext } = useUserDesignSlider(
    userDesignList,
    PER_PAGE_SP,
    PER_PAGE_PC,
  );

  // デザイン詳細モーダル用のstate
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // openModalUserPrintIdが渡された場合は該当デザインのモーダルを自動で開く
  useEffect(() => {
    if (!openModalUserPrintId || userDesignList.length === 0) return;
    const index = userDesignList.findIndex((d) => d.userPrintId === openModalUserPrintId);
    if (index >= 0) {
      setSelectedIndex(index);
      setIsModalOpen(true);
    }
  }, [openModalUserPrintId, userDesignList]);

  const spOptions = {
    type: 'loop',
    arrows: false,
    pagination: false,
    autoplay: false,
    clones: 0,
  };

  const pcOptions = {
    type: 'loop',
    autoplay: false,
    pagination: false,
    arrows: false,
    clones: 0,
    gap: 10,
    fixedWidth: false,
    fixedHeight: false,
  };

  // 画面サイズに応じたオプションを選択
  const splideOptions = isSp ? spOptions : pcOptions;

  // 画像クリック時の処理
  const handleImageClick = (item: UserDesignItem) => {
    const index = userDesignList.findIndex((design) => design.id === item.id);
    if (index >= 0) {
      setSelectedIndex(index);
      setIsModalOpen(true);
    }
  };

  // モーダルを閉じる処理
  const handleCloseUserDesignDetailModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <div className={styles.userDesignsContainer}>
        <div className={styles.titleSection}>
          <p className={styles.subTitle}>みんなのデザイン</p>
        </div>
        <div className={styles.sliderSection}>
          {slideItems.length > 0 && (
            <Splide
              key={isSp ? 'sp' : 'pc'}
              options={splideOptions}
              aria-label='User Designs'
              hasTrack={false}
              className={styles.splide}
              ref={splideRef}
            >
              <SplideTrack className={styles.splideTrack}>
                {slideItems.map((page) => (
                  <SplideSlide key={`slidePage-${page.id}`}>
                    <div className={styles.columnContainer}>
                      {page.items.map((item) => (
                        <div key={`slideItem-${item.id}`} className={styles.itemContent}>
                          <div className={styles.itemImageContainer} onClick={() => handleImageClick(item)}>
                            <Image
                              src={item.previewImageUrl}
                              alt={item.gfWorkName}
                              className={styles.itemImage}
                              width={100}
                              height={100}
                              style={{ objectFit: 'contain' }}
                            />
                          </div>
                          <p className={styles.itemInfo}>{item.gfWorkName}</p>
                        </div>
                      ))}
                    </div>
                  </SplideSlide>
                ))}
              </SplideTrack>
            </Splide>
          )}
          {/* 矢印ボタン */}
          <div className={styles.arrowContainer}>
            <button type='button' className={styles.customPrevArrow} onClick={handleSlidePrev}>
              <RoundArrowIcon direction='left' color='black' size='xLarge' isBold={true} />
            </button>
            <button type='button' className={styles.customNextArrow} onClick={handleSlideNext}>
              <RoundArrowIcon direction='right' color='black' size='xLarge' isBold={true} />
            </button>
          </div>
        </div>

        {/* デザイン詳細モーダル */}
        <UserDesignDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseUserDesignDetailModal}
          items={userDesignList}
          initialIndex={selectedIndex}
          getCallbackUrl={(item) => `/product/${item.productId}/detail?openModal=${item.userPrintId}` as Route}
          from={SIZE_SELECT_FROM.productDetail}
        />
      </div>
    </>
  );
}

export default ProductUserDesignsSlider;
